import React, { useState, useEffect, useContext, useRef } from 'react';
import { ProductModalImage } from 'web-store-modules/packages/modal-media';
import { isMobileDevice } from 'web-store-modules/packages/obsess-device-detector'

import { ImageTypes, ContentTypes } from '../../util/ProductModalEnums';
import { ThumbnailClicked, VideoIconClicked, ProductImageClicked } from '../../../../../../../utils/Analytics';
import { ProductModalContext } from '../../../ProductModalContext';

import './ProductModalCarousel.css';

const swipeThreshold = 200;

const nextArrowUrl = "https://cdn.obsessvr.com/next-thumbnail-arrow.png";
const threeIcon = 'https://cdn.obsessvr.com/3d-circle-icon.png';
const spinIcon = 'https://cdn.obsessvr.com/360-circle-icon.png';
const videoIcon = 'https://cdn.obsessvr.com/video-circle-icon.png';
const WISHLIST_ACTIVE_ICON_URL = "https://cdn.obsess-vr.com/fendi/icon_yellow.svg";
const WISHLIST_INACTIVE_ICON_URL = "https://cdn.obsess-vr.com/fendi/wishlist_heart_icon.svg";
const isMobile = isMobileDevice();

const ProductModalCarousel = ({ thumbnailArr, sku, setFocusMode, focusMode, priceNum, addToCartEnabled, isInWishlist, addToCartClicked }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [startPos, setStartPos] = useState(0);
    const [endPos, setEndPos] = useState(0);
    const [carouselHeight, setCarouselHeight] = useState();
    const [carouselWidth, setCarouselWidth] = useState();
    const [distOffset, setDistOffset] = useState(0);
    const [xTranslation, setXTranslation] = useState(0);
    const [isUserMoving, setIsUserMoving] = useState(false);
    const [isUserInteracting, setIsUserInteracting] = useState(false);
    const [startTime, setStartTime] = useState(0);
    const [endTime, setEndTime] = useState(0);
    const [exitingFocusMode, setExitingFocusMode] = useState(false);

    const { productInfo, modalOpened } = useContext(ProductModalContext);

    const carouselContainer = useRef();

    const onPlayVideo = () => {
        VideoIconClicked(productInfo[ContentTypes.SKU], productInfo[ContentTypes.TITLE]);
    }

    const renderThumbnails = () => {
        return thumbnailArr.map((thumbnail, index) => {
            return <ProductModalImage key={index} index={index} modalOpened={modalOpened} currentIndex={currentIndex} focusMode={focusMode} exitingFocusMode={exitingFocusMode} setFocusMode={setFocusMode} xTranslation={xTranslation} isUserMoving={isUserMoving} setIsUserInteracting={setIsUserInteracting} onPlayVideo={onPlayVideo} {...thumbnail} />
        })
    }

    const getSign = (num) => Math.sign(num);

    const withinBounds = (index, min, max) => (index < min || index + 1 > max) ? false : true;

    const onMouseDown = (e) => {
        e.preventDefault();
        setStartPos(e.clientX);
        setStartTime(Date.now())
        setIsUserMoving(true);
    }

    const onMouseMove = (e) => {
        e.preventDefault();
        if (isUserMoving && !isUserInteracting) {
            setEndPos(e.clientX);
            setEndTime(Date.now());
        }
    }

    const onMouseUp = (e) => {
        e.preventDefault();
        const dist = startPos - endPos;
        const isUserSwiping = Math.abs(startTime - endTime) < swipeThreshold ? true : false;

        if ((Math.abs(dist) > (carouselWidth * 0.4) || isUserSwiping) && endPos) {
            if (withinBounds(currentIndex + (getSign(dist) * 1), 0, thumbnailArr.length)) {
                setCurrentIndex(currentIndex + (getSign(dist) * 1));
            }
        }

        setStartPos(0);
        setEndPos(0);
        setIsUserMoving(false);
    }

    const onTouchStart = (e) => {
        setStartPos(e.touches[0].pageX);
        setStartTime(Date.now())
        if (!carouselWidth) {
            setCarouselWidth((carouselContainer.current.clientWidth));
        }
    }

    const onTouchMove = (e) => {
        if (!isUserInteracting) {
            setEndPos(e.touches[0].pageX);
            setEndTime(Date.now());

            setIsUserMoving(true);
        }
    }

    const onTouchEnd = () => {
        const dist = startPos - endPos;
        const isUserSwiping = Math.abs(startTime - endTime) < swipeThreshold ? true : false;

        if ((Math.abs(dist) > (carouselWidth * 0.4) || isUserSwiping) && endPos) {
            if (withinBounds(currentIndex + (getSign(dist) * 1), 0, thumbnailArr.length)) {
                setCurrentIndex(currentIndex + (getSign(dist) * 1));
            }
        }

        setStartPos(0);
        setEndPos(0);
        setIsUserMoving(false);
    }

    const onLeftArrowClick = () => {
        ProductImageClicked(productInfo[ContentTypes.TITLE]);
        if (currentIndex !== 0) {
            setCurrentIndex(currentIndex - 1);
        }
    }

    const onRightArrowClick = () => {
        ProductImageClicked(productInfo[ContentTypes.TITLE]);
        if (currentIndex !== thumbnailArr.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    }

    useEffect(() => {
        setCarouselWidth(carouselContainer.current.clientWidth);
        setCarouselHeight(carouselContainer.current.clientHeight);
    }, [])

    // Used to resize carousel width when entering focus mode
    useEffect(() => {
        setCarouselWidth(carouselContainer.current.clientWidth);
        if (!focusMode) {
            // used to keep transition between focus mode/normal mode and prevent carousel from scrolling back to last position after resizing width
            setExitingFocusMode(!focusMode);
            setTimeout(() => {
                setExitingFocusMode(false);
            }, 250)
        }
    }, [focusMode])

    // If product variant changes, set index back to 0
    useEffect(() => {
        setCurrentIndex(0);
    }, [sku])

    useEffect(() => {
        setDistOffset(carouselWidth * currentIndex);
    }, [currentIndex, carouselWidth])

    useEffect(() => {
        if (thumbnailArr.length > 1) {
            setXTranslation(distOffset ? -distOffset : 0);
        }
    }, [distOffset])

    useEffect(() => {
        if ((thumbnailArr.length > 1) || isUserInteracting) {
            setXTranslation(-(distOffset + (startPos - endPos)));
        }
    }, [endPos, isUserInteracting])

    const imageWrapperStyle = { height: carouselHeight ? carouselHeight : '100%' }

    if (priceNum == undefined) {
        imageWrapperStyle.paddingTop = '0.5em';
        imageWrapperStyle.paddingBottom = '0.5em';
    }

    const productImageClick = () => {
        ThumbnailClicked(productInfo[ContentTypes.TITLE]);
    }

    return (
        <div id={`${isMobile ? 'product-image-carousel' : 'product-image-carousel-desktop'}`} className="flex-1" style={{ paddingLeft: '0%', maxHeight: '374px' }}>

            {addToCartEnabled && <div onClick={addToCartClicked} className="icon-heart" style={{ backgroundImage: isInWishlist ? `url(${WISHLIST_ACTIVE_ICON_URL})` : `url(${WISHLIST_INACTIVE_ICON_URL})` }}></div>}

            {!focusMode && <div id="carousel-arrow-container">
                <img id="carousel-left-arrow" style={{ opacity: currentIndex !== 0 ? '1' : '0' }} src={nextArrowUrl} onClick={onLeftArrowClick} />
                <img id="carousel-right-arrow" style={{ opacity: currentIndex !== thumbnailArr.length - 1 ? '1' : '0' }} src={nextArrowUrl} onClick={onRightArrowClick} />
            </div>}
            <div
                className='product-image-wrapper'
                ref={carouselContainer}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                style={imageWrapperStyle}
                onClick={productImageClick}
            >
                {(thumbnailArr && carouselHeight) ? renderThumbnails() : null}
            </div>
        </div>
    );
}

export default ProductModalCarousel;
